import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'

import { Flex, hexToRgb, Position, relativeLuminance, Text } from '@cash-design-system/react'
import { FormBlockerElementCaptionedTileElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'

import { SquiggleSvg } from './SquiggleSvg'

const Tile = styled.div<{ backgroundColor?: string }>`
  position: relative;
  width: 7.25rem;
  height: 11.25rem;
  border-radius: 1.5rem;
  ${props => (props.backgroundColor ? `background-color: ${props.backgroundColor}` : '')};
  padding: ${({ theme }) => theme.spacing.m};
`

const Icon = styled.img`
  position: absolute;
  top: 1rem;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
`

const Caption = styled.div`
  height: 2rem;
  position: absolute;
  bottom: 0.825rem;
  width: 100%;
  padding: 0 0.875rem;

  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
`

const CaptionText = styled(Text)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const typographyByTextLength = (text: string) => {
  if (text.length > 6) return 'body'
  return 'sectionTitle'
}

export const CaptionedTileElement = ({
  icon,
  background_color,
  caption,
  accessibility_text,
}: FormBlockerElementCaptionedTileElement) => {
  const theme = useTheme()

  const backgroundColor = theme.darkMode ? background_color?.dark?.srgb : background_color?.light?.srgb
  const iconUrl = theme.darkMode ? icon?.dark_url : icon?.light_url

  // this is how mobile decides whether to use light or dark text color
  // if background color is darker than backgroundProminent then use light color; if not use dark color
  // flip values on dark mode
  const lightColor = 'constantWhite'
  const darkColor = 'constantBlack'
  const textColor = backgroundColor
    ? relativeLuminance(hexToRgb(backgroundColor)) <
      relativeLuminance(hexToRgb(theme.arcadeColors.semantic.backgroundProminent))
      ? lightColor
      : darkColor
    : lightColor

  return (
    <Flex justifyContent="center">
      <Tile backgroundColor={backgroundColor} role="img" aria-label={accessibility_text}>
        {iconUrl && (
          <Flex justifyContent="center">
            <Icon src={iconUrl} data-testid="tile-icon" />
          </Flex>
        )}
        <Position position="absolute" top="4.2rem" left="0">
          <SquiggleSvg color={theme.arcadeColors.base[textColor]} />
        </Position>
        {caption && (
          <Flex justifyContent="center">
            <Caption>
              <CaptionText color={textColor} typography={typographyByTextLength(caption)}>
                {caption}
              </CaptionText>
            </Caption>
          </Flex>
        )}
      </Tile>
    </Flex>
  )
}
